import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Switch,
  Radio,
  InputNumber,
  Space,
  Alert,
  Divider,
  Typography,
  Row,
  Col,
  Tooltip,
  message,
  Image,
  Card,
  Empty,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { 
  DisplayConfigModalProps, 
  ItemMasterData, 
  DisplayMethod, 
  DisplayMethodOption 
} from '../../../types/item-master-data';

const { Title, Text } = Typography;

// 图片展示组件
const ImagePreview: React.FC<{ images?: string[]; title: string; t: any }> = ({ images, title, t }) => {
  if (!images || images.length === 0) {
    return (
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ fontSize: 12 }}>{title}:</Text>
        <div style={{ 
          width: 60, 
          height: 60, 
          background: '#f5f5f5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: 4,
          fontSize: 12,
          color: '#999',
          border: '1px dashed #d9d9d9'
        }}>
          <span>{t('itemMasterData.displayConfig.images.noImage')}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <Text strong style={{ fontSize: 12 }}>{title}:</Text>
      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {images.slice(0, 3).map((img, index) => (
          <Image
            key={index}
            src={img}
            alt={`${title} ${index + 1}`}
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            placeholder={<div style={{ width: 60, height: 60, background: '#f0f0f0' }} />}
            fallback="/placeholder-image.png" // 图片加载失败时的占位图
          />
        ))}
        {images.length > 3 && (
          <div style={{ 
            width: 60, 
            height: 60, 
            background: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: 4,
            fontSize: 12
          }}>
            +{images.length - 3}
          </div>
        )}
      </div>
    </div>
  );
};

const DisplayConfigModal: React.FC<DisplayConfigModalProps> = ({
  visible,
  item,
  onClose,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // 陈列方式选项
  const displayMethodOptions: DisplayMethodOption[] = [
    {
      value: 'front',
      label: t('itemMasterData.displayConfig.oritation.front'),
      description: t('itemMasterData.displayConfig.oritation.frontDesc'),
    },
    {
      value: 'side',
      label: t('itemMasterData.displayConfig.oritation.side'),
      description: t('itemMasterData.displayConfig.oritation.sideDesc'),
    },
    {
      value: 'top',
      label: t('itemMasterData.displayConfig.oritation.top'),
      description: t('itemMasterData.displayConfig.oritation.topDesc'),
    },
  ];

  // 初始化表单数据
  useEffect(() => {
    if (visible && item) {
      const defaultConfig = {
        allowShelfDisplay: true,  // 默认打开，不可编辑
        allowHole: false,         // 默认关闭，可编辑
        oritation: 'front',       // 默认正面朝前
        allowStack: false,        // 默认关闭，可编辑
        allowNest: false,         // 默认关闭，可编辑
        nestHeight: undefined,
      };

      // 从item中提取陈列配置字段
      const config = {
        allowShelfDisplay: item.allowShelfDisplay ?? defaultConfig.allowShelfDisplay,
        allowHole: item.allowHole ?? defaultConfig.allowHole,
        oritation: item.oritation ?? defaultConfig.oritation,
        allowStack: item.allowStack ?? defaultConfig.allowStack,
        allowNest: item.allowNest ?? defaultConfig.allowNest,
        nestHeight: item.nestHeight ?? defaultConfig.nestHeight,
      };

      form.setFieldsValue(config);
    }
  }, [visible, item, form]);

  // 处理表单提交
  const handleSubmit = async () => {
    if (!item) return;

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // 创建包含所有字段的对象，包括原有的ItemMasterData字段和新的陈列配置字段
      const updatedItem = {
        ...item,
        ...values,
        displayConfigGmtModified: Date.now(),
        displayConfigModifierAccount: 'current_user', // TODO: 从认证上下文获取
      };

      await onSave(item.code, updatedItem);
      message.success(t('itemMasterData.displayConfig.saveSuccess'));
      onClose();
    } catch (error: any) {
      console.error('保存陈列参数配置失败:', error);
      if (error.errorFields) {
        message.error(t('itemMasterData.displayConfig.validateFailed'));
      } else {
        message.error(t('itemMasterData.displayConfig.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // 监听嵌套开关变化
  const handleNestingChange = (checked: boolean) => {
    if (!checked) {
      form.setFieldValue('nestHeight', undefined);
    }
  };

  // 监听孔洞开关变化
  const handleHoleChange = (checked: boolean) => {
    if (checked) {
      // 开启孔洞时，关闭叠放
      form.setFieldsValue({ allowStack: false });
    }
  };

  // 监听叠放开关变化
  const handleStackChange = (checked: boolean) => {
    if (checked) {
      // 开启叠放时，关闭孔洞
      form.setFieldsValue({ allowHole: false });
    }
  };

  return (
    <Modal
      title={
        <Space>
          <span>{t('itemMasterData.displayConfig.title')}</span>
          {item && (
            <Text type="secondary" code>
              {item.code} - {item.name}
            </Text>
          )}
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={800}
      confirmLoading={submitting}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnClose
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          {/* 基础陈列配置 */}
          <Title level={5}>{t('itemMasterData.displayConfig.basicSettings')}</Title>
          
          <Row gutter={24}>
            {/* <Col span={12}>
              <Form.Item
                name="allowShelfDisplay"
                label={
                  <Space>
                    {t('itemMasterData.displayConfig.allowShelfDisplay')}
                    <Tooltip title={t('itemMasterData.displayConfig.allowShelfDisplayTip')}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch disabled />
              </Form.Item>
              <Alert
                message={t('itemMasterData.displayConfig.allowShelfDisplayTip')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col> */}

            <Col span={12}>
              <Form.Item
                name="allowHole"
                label={
                  <Space>
                    {t('itemMasterData.displayConfig.allowHole')}
                    <Tooltip title={t('itemMasterData.displayConfig.allowHoleTip')}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch onChange={handleHoleChange} />
              </Form.Item>
              <Alert
                message={t('itemMasterData.displayConfig.allowHoleTip')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
          </Row>

          <Divider />

          {/* 陈列方式配置 */}
          <Title level={5}>{t('itemMasterData.displayConfig.displayMethodSettings')}</Title>
          
          <Form.Item
            name="oritation"
            label={t('itemMasterData.displayConfig.oritation.label')}
            rules={[
              { required: true, message: t('itemMasterData.displayConfig.oritation.required') }
            ]}
          >
            <Radio.Group>
              <Space direction="vertical" style={{ width: '100%' }}>
                {displayMethodOptions.map((option) => (
                  <Card 
                    key={option.value} 
                    size="small" 
                    style={{ 
                      width: '100%', 
                      borderColor: form.getFieldValue('oritation') === option.value ? '#1890ff' : undefined,
                      borderWidth: form.getFieldValue('oritation') === option.value ? 2 : 1
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Radio value={option.value} style={{ flex: '0 0 auto' }}>
                        <Space direction="vertical" size={0}>
                          <span>{option.label}</span>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {option.description}
                          </Text>
                        </Space>
                      </Radio>
                      <div style={{ flex: '1 1 auto', marginLeft: 16 }}>
                        {/* 根据选项类型展示对应的图片 */}
                        {option.value === 'front' && item && (
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            <ImagePreview images={item.frontImageList} title={t('itemMasterData.displayConfig.images.front')} t={t} />
                            <ImagePreview images={item.frontImageList4Pdq} title={t('itemMasterData.displayConfig.images.frontPdq')} t={t} />
                          </div>
                        )}
                        {option.value === 'side' && item && (
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            <ImagePreview images={item.sideImageList} title={t('itemMasterData.displayConfig.images.side')} t={t} />
                            <ImagePreview images={item.sideImageList4Pdq} title={t('itemMasterData.displayConfig.images.sidePdq')} t={t} />
                          </div>
                        )}
                        {option.value === 'top' && item && (
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            <ImagePreview images={item.topImageList} title={t('itemMasterData.displayConfig.images.top')} t={t} />
                            <ImagePreview images={item.topImageList4Pdq} title={t('itemMasterData.displayConfig.images.topPdq')} t={t} />
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>

          <Divider />

          {/* 叠放配置 */}
          <Title level={5}>{t('itemMasterData.displayConfig.stackingSettings')}</Title>
          
          <Form.Item
            name="allowStack"
            label={
              <Space>
                {t('itemMasterData.displayConfig.allowStack')}
                <Tooltip title={t('itemMasterData.displayConfig.allowStackTip')}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch onChange={handleStackChange} />
          </Form.Item>
          <Alert
            message={t('itemMasterData.displayConfig.allowStackTip')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider />

          {/* 嵌套配置 */}
          <Title level={5}>{t('itemMasterData.displayConfig.nestingSettings')}</Title>
          
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="allowNest"
                label={
                  <Space>
                    {t('itemMasterData.displayConfig.allowNest')}
                    <Tooltip title={t('itemMasterData.displayConfig.allowNestTip')}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                valuePropName="checked"
              >
                <Switch onChange={handleNestingChange} />
              </Form.Item>
              <Alert
                message={t('itemMasterData.displayConfig.allowNestTip')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.allowNest !== currentValues.allowNest
                }
              >
                {({ getFieldValue }) => {
                  const allowNest = getFieldValue('allowNest');
                  return (
                    <Form.Item
                      name="nestHeight"
                      label={t('itemMasterData.displayConfig.nestHeight')}
                      rules={
                        allowNest
                          ? [
                              { required: true, message: t('itemMasterData.displayConfig.nestHeightRequired') },
                              { type: 'number', min: 0.1, message: t('itemMasterData.displayConfig.heightMin') },
                              { type: 'number', max: 1000, message: t('itemMasterData.displayConfig.heightMax') },
                            ]
                          : []
                      }
                      style={{ display: allowNest ? 'block' : 'none' }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        addonAfter={t('itemMasterData.displayConfig.heightUnit')}
                        placeholder={t('itemMasterData.displayConfig.heightPlaceholder')}
                        min={0.1}
                        max={1000}
                        precision={1}
                        disabled={!allowNest}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default DisplayConfigModal;